/**
 * useUserProgression — Lightweight progression state derived from existing data.
 *
 * Steps:
 *   1. Complete profile
 *   2. Create first program OR session
 *   3. Place sessions in planning
 *   4. Complete first workout
 *   5. Unlock deeper controls (interface_mode >= standard)
 *   6. Discover advanced modules (interface_mode = advanced)
 *
 * No new DB tables. Pure derivation from existing queries + localStorage.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type JourneyStep = 1 | 2 | 3 | 4 | 5 | 6;

export interface ProgressionState {
  currentStep: JourneyStep;
  profileComplete: boolean;
  hasContent: boolean;        // program or session exists
  hasPlanning: boolean;       // planning_week_templates has blocks
  hasFirstWorkout: boolean;   // at least 1 checkin with training_done
  hasStandardMode: boolean;   // interface_mode !== 'simple'
  hasAdvancedMode: boolean;   // interface_mode === 'advanced'
  totalSteps: 6;
  completedSteps: number;
  isFullyComplete: boolean;
  loading: boolean;
}

async function fetchProgressionData(userId: string) {
  const [profileRes, programsRes, sessionsRes, planningRes, checkinRes] = await Promise.all([
    supabase
      .from("profiles")
      .select("onboarding_completed, interface_mode")
      .eq("user_id", userId)
      .maybeSingle(),
    supabase
      .from("programs")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .not("status", "in", "(generating,error)"),
    supabase
      .from("sessions")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId),
    supabase
      .from("planning_week_templates")
      .select("blocks")
      .eq("user_id", userId)
      .limit(1)
      .maybeSingle(),
    supabase
      .from("program_checkins")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("training_done", true),
  ]);

  const profile = profileRes.data;
  const programCount = programsRes.count ?? 0;
  const sessionCount = sessionsRes.count ?? 0;
  const planningBlocks = planningRes.data?.blocks;
  const checkinCount = checkinRes.count ?? 0;

  const hasBlocks = Array.isArray(planningBlocks) && planningBlocks.length > 0;
  const interfaceMode = (profile as any)?.interface_mode ?? "simple";

  return {
    profileComplete: !!(profile as any)?.onboarding_completed,
    hasContent: programCount > 0 || sessionCount > 0,
    hasPlanning: hasBlocks,
    hasFirstWorkout: checkinCount > 0,
    hasStandardMode: interfaceMode !== "simple",
    hasAdvancedMode: interfaceMode === "advanced",
  };
}

export function useUserProgression(): ProgressionState {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["user-progression", user?.id],
    queryFn: () => fetchProgressionData(user!.id),
    enabled: !!user,
    staleTime: 30_000,
  });

  return useMemo(() => {
    if (!data) {
      return {
        currentStep: 1 as JourneyStep,
        profileComplete: false,
        hasContent: false,
        hasPlanning: false,
        hasFirstWorkout: false,
        hasStandardMode: false,
        hasAdvancedMode: false,
        totalSteps: 6,
        completedSteps: 0,
        isFullyComplete: false,
        loading: isLoading,
      };
    }

    const flags = [
      data.profileComplete,
      data.hasContent,
      data.hasPlanning,
      data.hasFirstWorkout,
      data.hasStandardMode,
      data.hasAdvancedMode,
    ];

    const completedSteps = flags.filter(Boolean).length;

    // Current step = first incomplete step
    let currentStep: JourneyStep = 6;
    for (let i = 0; i < flags.length; i++) {
      if (!flags[i]) {
        currentStep = (i + 1) as JourneyStep;
        break;
      }
    }

    return {
      currentStep,
      ...data,
      totalSteps: 6,
      completedSteps,
      isFullyComplete: completedSteps >= 6,
      loading: isLoading,
    };
  }, [data, isLoading]);
}
